import { dealerSelectors } from '@makemydeal/dr-shared-store';
import type { CustomerCardStoreState } from '../types/CustomerCardTypes';
import type { BusinessEntity, CreateBusinessRequest, AssociateBusinessRequest } from '../types/businessConversion.types';
import { getCustomerCardBffBaseUrl } from '../config';
import * as customerCardSelectors from './selectors';
import {
    CREATE_BUSINESS_REQUEST,
    CREATE_BUSINESS_SUCCESS,
    CREATE_BUSINESS_FAILURE,
    ASSOCIATE_BUSINESS_REQUEST,
    ASSOCIATE_BUSINESS_SUCCESS,
    ASSOCIATE_BUSINESS_FAILURE
} from './businessConversionActions';
import { SEARCH_REQUEST, SEARCH_SUCCESS, SEARCH_FAILURE } from './cardOperationActions';

type BusinessThunkDispatch = (action: any) => any;
type BusinessThunkGetState = () => CustomerCardStoreState & Parameters<typeof dealerSelectors.getDealerId>[0];

const businessHeaders = (dealId: string, dealerId: string, commonOrgId: string): Record<string, string> => ({
    'Content-Type': 'application/json',
    'x-dr-dealxgid': dealId,
    'x-dr-dealerid': dealerId,
    'x-dr-dealer-common-org-id': commonOrgId
});

export const searchBusinessByName = (query: string) => async (dispatch: BusinessThunkDispatch, getState: BusinessThunkGetState) => {
    const state = getState();
    const bffBaseUrl = getCustomerCardBffBaseUrl();
    const dealId = customerCardSelectors.getDealId(state);
    const dealerId = String(dealerSelectors.getDealerId(state));
    const commonOrgId = customerCardSelectors.getCommonOrgId(state) || '';

    if (!bffBaseUrl || !dealId) {
        dispatch({ type: SEARCH_FAILURE, payload: 'Missing configuration' });
        return;
    }

    dispatch({ type: SEARCH_REQUEST, payload: query });

    try {
        const response = await fetch(`${bffBaseUrl}/deal/${dealId}/business/search`, {
            method: 'POST',
            headers: businessHeaders(dealId, dealerId, commonOrgId),
            body: JSON.stringify({ query })
        });

        const responseBody = await response.json().catch(() => ({}));

        if (response.ok && responseBody?.ok) {
            dispatch({
                type: SEARCH_SUCCESS,
                payload: (responseBody.results as BusinessEntity[]) || []
            });
        } else {
            dispatch({ type: SEARCH_FAILURE, payload: 'SearchFailed' });
        }
    } catch {
        dispatch({ type: SEARCH_FAILURE, payload: 'SearchFailed' });
    }
};

export const createBusiness =
    (requestBody: CreateBusinessRequest) => async (dispatch: BusinessThunkDispatch, getState: BusinessThunkGetState) => {
        const state = getState();
        const bffBaseUrl = getCustomerCardBffBaseUrl();
        const dealId = customerCardSelectors.getDealId(state);
        const dealerId = String(dealerSelectors.getDealerId(state));
        const commonOrgId = customerCardSelectors.getCommonOrgId(state) || '';

        dispatch({ type: CREATE_BUSINESS_REQUEST });

        try {
            const response = await fetch(`${bffBaseUrl}/deal/${dealId}/business/create`, {
                method: 'POST',
                headers: businessHeaders(dealId!, dealerId, commonOrgId),
                body: JSON.stringify(requestBody)
            });

            const responseBody = await response.json().catch(() => ({}));
            const { commonConsumerId, businessEntityId } = responseBody ?? {};
            const hasRequiredIds = Boolean(commonConsumerId && businessEntityId);

            if (response.ok && responseBody?.ok && hasRequiredIds) {
                dispatch({ type: CREATE_BUSINESS_SUCCESS, payload: responseBody });
                return { ok: true as const, data: responseBody };
            }

            const error = responseBody?.error?.message || responseBody?.message || 'CreateBusinessFailed';
            dispatch({ type: CREATE_BUSINESS_FAILURE, payload: error });
            return { ok: false as const, error };
        } catch (error) {
            const errorMessage = (error as Error)?.message || 'CreateBusinessFailed';
            dispatch({ type: CREATE_BUSINESS_FAILURE, payload: errorMessage });
            return { ok: false as const, error: errorMessage };
        }
    };

export const associateBusiness =
    (requestBody: AssociateBusinessRequest) => async (dispatch: BusinessThunkDispatch, getState: BusinessThunkGetState) => {
        const state = getState();
        const bffBaseUrl = getCustomerCardBffBaseUrl();
        const dealId = customerCardSelectors.getDealId(state);
        const dealerId = String(dealerSelectors.getDealerId(state));
        const commonOrgId = customerCardSelectors.getCommonOrgId(state) || '';

        dispatch({ type: ASSOCIATE_BUSINESS_REQUEST });

        try {
            const response = await fetch(`${bffBaseUrl}/deal/${dealId}/business/associate`, {
                method: 'POST',
                headers: businessHeaders(dealId!, dealerId, commonOrgId),
                body: JSON.stringify(requestBody)
            });

            const responseBody = await response.json().catch(() => ({}));

            if (response.ok && responseBody?.ok) {
                dispatch({ type: ASSOCIATE_BUSINESS_SUCCESS, payload: responseBody });
                return { ok: true as const, data: responseBody };
            }

            const error = responseBody?.error?.message || responseBody?.message || 'AssociateBusinessFailed';
            dispatch({ type: ASSOCIATE_BUSINESS_FAILURE, payload: error });
            return { ok: false as const, error };
        } catch (error) {
            const errorMessage = (error as Error)?.message || 'AssociateBusinessFailed';
            dispatch({ type: ASSOCIATE_BUSINESS_FAILURE, payload: errorMessage });
            return { ok: false as const, error: errorMessage };
        }
    };

export type BusinessConversionAction =
    | { type: typeof CREATE_BUSINESS_REQUEST }
    | { type: typeof CREATE_BUSINESS_SUCCESS; payload: Record<string, unknown> }
    | { type: typeof CREATE_BUSINESS_FAILURE; payload: string }
    | { type: typeof ASSOCIATE_BUSINESS_REQUEST }
    | { type: typeof ASSOCIATE_BUSINESS_SUCCESS; payload: Record<string, unknown> }
    | { type: typeof ASSOCIATE_BUSINESS_FAILURE; payload: string };
